import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import {
  CORRECT_COLOR,
  INCORRECT_COLOR,
  ITextOption,
  illustrationSVG,
} from '~/pages/anatomyV2/libs/components/DiagnosisQuestionTemplate/constants'
import { DiagnosisQuestionTemplateStyles as S } from '~/pages/anatomyV2/libs/components/DiagnosisQuestionTemplate/styles'
import SectionProgress from '~/pages/anatomyV2/libs/components/SectionProgress'
import { useMatchHeaderBackground } from '~/pages/anatomyV2/libs/hooks/useMatchHeaderBackground'
import { useNextStep } from '~/shared/hooks/useNextStep'
import { TPageProps } from '~/shared/types/commonTypes'

interface IDiagnosisQuestionTemplateProps extends TPageProps {
  currentSection: number
  titleI18nKey: string
  caseTextI18nKey: string
  options: ITextOption[]
  correctOptionKey: string
  correctTitleI18nKey: string
  correctExplanationI18nKey: string
  incorrectTitleI18nKey: string
  incorrectExplanationI18nKey: string
  question: string
}

const ResultIcon = ({ isCorrect }: { isCorrect: boolean }) => (
  <svg fill="none" height="20" viewBox="0 0 20 20" width="20">
    <circle
      cx="10"
      cy="10"
      fill={isCorrect ? CORRECT_COLOR : INCORRECT_COLOR}
      r="10"
    />
    {isCorrect ? (
      <path
        d="M6 10.5l2.5 2.5L14 7"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    ) : (
      <path
        d="M10 5.5v6M10 14.2h.01"
        stroke="white"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
    )}
  </svg>
)

const DiagnosisQuestionTemplate = ({
  pageId,
  nextPagePath,
  currentSection,
  titleI18nKey,
  caseTextI18nKey,
  options,
  correctOptionKey,
  correctTitleI18nKey,
  correctExplanationI18nKey,
  incorrectTitleI18nKey,
  incorrectExplanationI18nKey,
  question,
}: IDiagnosisQuestionTemplateProps) => {
  useMatchHeaderBackground()

  const { t } = useTranslation('anatomyV2')
  const [answer, setAnswer] = useState<string | null>(null)

  const handleContinue = useNextStep({
    nextPagePath,
    pageId,
    pageName: pageId,
    question,
  })

  const isCorrect = answer === correctOptionKey

  return (
    <S.Root>
      <S.ContentWrapper>
        <SectionProgress currentSection={currentSection} pageId={pageId} />

        <S.Title>
          <Trans ns="anatomyV2" i18nKey={titleI18nKey} />
        </S.Title>

        <S.CaseBoxWrapper>
          <S.CaseBox>
            {answer === null ? (
              <S.CaseText>
                <Trans ns="anatomyV2" i18nKey={caseTextI18nKey} />
              </S.CaseText>
            ) : (
              <S.ResultBody>
                <S.ResultHeader>
                  <ResultIcon isCorrect={isCorrect} />
                  <S.ResultTitle $isCorrect={isCorrect}>
                    <Trans
                      ns="anatomyV2"
                      i18nKey={
                        isCorrect ? correctTitleI18nKey : incorrectTitleI18nKey
                      }
                    />
                  </S.ResultTitle>
                </S.ResultHeader>

                <S.ResultExplanation>
                  <Trans
                    ns="anatomyV2"
                    i18nKey={
                      isCorrect
                        ? correctExplanationI18nKey
                        : incorrectExplanationI18nKey
                    }
                  />
                </S.ResultExplanation>
              </S.ResultBody>
            )}
          </S.CaseBox>

          <S.Illustration src={illustrationSVG} alt="" />
        </S.CaseBoxWrapper>

        {answer === null ? (
          <S.Options>
            {options.map(({ key, i18nKey }) => (
              <S.OptionButton
                key={key}
                type="button"
                onClick={() => setAnswer(key)}
              >
                {t(i18nKey)}
              </S.OptionButton>
            ))}
          </S.Options>
        ) : (
          <S.StyledButton type="button" onClick={() => handleContinue(answer)}>
            <Trans i18nKey="actions.continue" />
          </S.StyledButton>
        )}
      </S.ContentWrapper>
    </S.Root>
  )
}

export default DiagnosisQuestionTemplate
